import React, { useEffect, useRef, useState } from 'react'
import CloseIcon from '@material-ui/icons/Close'
import { useSnackbar } from 'notistack'
import * as Sentry from '@sentry/react'
import { SurveyState, useMarkSurveyMutation, useSubmitSurveyResponseMutation } from '@paintscout/api'
import type { MySurveyFieldsFragment } from '@paintscout/api'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  makeStyles,
  Typography
} from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import { track } from 'shared/util/analytics'
import SurveyQuestionField from './SurveyQuestionField'
import type { SurveyDraft } from './surveyForm'
import { checkDraft, setChoice, setRating, setText } from './surveyForm'

const useStyles = makeStyles((theme: Theme) => ({
  title: {
    display: 'block',
    paddingRight: theme.spacing(4)
  },
  close: {
    position: 'absolute',
    top: theme.spacing(1),
    right: theme.spacing(1)
  },
  description: {
    marginBottom: theme.spacing(2),
    whiteSpace: 'pre-line'
  }
}))

export interface SurveyDialogProps {
  survey: MySurveyFieldsFragment
  /** Closes the dialog. Called after a stored response or when the user closes it. */
  onClose: () => void
}

/**
 * One in-house survey (#25349), every question on one page. Marks the survey `viewed` when it
 * opens and `dismissed` when the user closes it unanswered; submits once through
 * `submitSurveyResponse`. Behind `inHouseSurveys` (SurveyCenter opens it).
 */
export default function SurveyDialog({ survey, onClose }: SurveyDialogProps) {
  const classes = useStyles()
  const { enqueueSnackbar } = useSnackbar()
  const [markSurvey] = useMarkSurveyMutation()
  const [submitSurveyResponse] = useSubmitSurveyResponseMutation()
  const [draft, setDraft] = useState<SurveyDraft>({})
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const titleId = `survey-title-${survey.id}`
  const eventProps = { surveyId: survey.id, trigger: survey.trigger, key: survey.key ?? undefined }

  // Once per open, even under StrictMode's double effect run.
  const startedRef = useRef(false)
  useEffect(() => {
    if (startedRef.current) return
    startedRef.current = true
    track('survey_started', eventProps)
    if (!survey.viewedAt) {
      markSurvey({ variables: { id: survey.id, state: SurveyState.Viewed } }).catch(() => {
        // The survey expired or was archived since it loaded; submitting will say so.
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function handleClose() {
    if (submitting) return
    track('survey_dismissed', eventProps)
    if (!survey.dismissedAt) {
      markSurvey({ variables: { id: survey.id, state: SurveyState.Dismissed } }).catch(() => {
        // Nothing to tell the user: they closed it.
      })
    }
    onClose()
  }

  async function handleSubmit() {
    const checked = checkDraft(survey.questions, draft)
    setErrors(checked.errors)
    if (!checked.valid) return
    setSubmitting(true)
    setSubmitError(null)
    try {
      await submitSurveyResponse({ variables: { id: survey.id, answers: checked.answers } })
      track('survey_completed', eventProps)
      enqueueSnackbar('Thanks for your feedback!', { variant: 'success' })
      onClose()
    } catch (error) {
      // The user loses their answers here, so report it.
      Sentry.captureException(error, { tags: { surveyId: survey.id } })
      setSubmitError('We could not save your answers. Please try again.')
      setSubmitting(false)
    }
  }

  function update(next: SurveyDraft, questionId: string) {
    setDraft(next)
    setErrors((current) => {
      if (!current[questionId]) return current
      const { [questionId]: _cleared, ...rest } = current
      return rest
    })
  }

  return (
    <Dialog
      open
      // A stray click outside must not dismiss an on-login survey for good; Escape and the buttons do.
      onClose={(_event: unknown, reason?: string) => {
        if (reason !== 'backdropClick') handleClose()
      }}
      maxWidth="sm"
      fullWidth
      aria-labelledby={titleId}
    >
      <DialogTitle>
        <span id={titleId} className={classes.title}>
          {survey.title}
        </span>
      </DialogTitle>
      <IconButton className={classes.close} size="small" onClick={handleClose} aria-label="Close survey">
        <CloseIcon fontSize="small" />
      </IconButton>
      <DialogContent>
        {survey.description && (
          <Typography className={classes.description} variant="body2" color="textSecondary">
            {survey.description}
          </Typography>
        )}
        {survey.questions.map((question) => (
          <SurveyQuestionField
            key={question.id}
            question={question}
            value={draft[question.id]}
            error={errors[question.id]}
            disabled={submitting}
            onChoice={(value, checked) => update(setChoice(draft, question, value, checked), question.id)}
            onRating={(rating) => update(setRating(draft, question, rating), question.id)}
            onText={(text) => update(setText(draft, question, text), question.id)}
          />
        ))}
        {submitError && (
          <Typography variant="body2" color="error" role="alert">
            {submitError}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={handleClose} disabled={submitting}>
          Not now
        </Button>
        <Button variant="contained" color="primary" onClick={handleSubmit} disabled={submitting}>
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  )
}
